// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  input,
  OnChanges,
  output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, Subject } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { SpFilterModel } from '../model/sp-filter-model';
import { SpModalFilters } from '../model/sp-modal-filters';
import { SparePartsDatepickerComponent } from '../spare-parts-datepicker/spare-parts-datepicker.component';
import { SparePartsFilterPopoverComponent } from '../spare-parts-filter-popover/spare-parts-filter-popover.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SparePartsDatepickerComponent,
    SparePartsFilterPopoverComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-spare-parts-filter',
  templateUrl: './spare-parts-filter.component.html',
  styleUrl: './spare-parts-filter.component.scss',
})
export class SparePartsFilterComponent implements OnChanges {
  filter = input.required<SpFilterModel>();
  filterChange = output<SpFilterModel>();
  dateRange = input<string>('');
  dateRangeChange = output<string>();
  disableSearch = input<boolean>(false);
  disableModal = input<boolean>(false);

  protected modalCount = 0;
  protected popoverVisibility = false;

  private readonly searchSubject = new Subject<string>();

  public constructor() {
    this.listenToSubject();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter']?.currentValue) {
      this.updateModalCount();
    }
  }

  private listenToSubject() {
    this.searchSubject
      .pipe(takeUntilDestroyed(), debounceTime(300))
      .subscribe(this.searchSubjectFn);
  }

  private readonly searchSubjectFn = (value: string) => {
    this.filter().searchInput = value;
    this.filterChange.emit(this.filter());
  };

  protected onDateRangeChange(dateRange: string): void {
    this.dateRangeChange.emit(dateRange);
  }

  protected onFilterClick(): void {
    this.popoverVisibility = true;
  }

  protected onModalFiltersChange(modalFilters: SpModalFilters): void {
    this.filter().modalFilters.merge(modalFilters);
    this.updateModalCount();
    this.filterChange.emit(this.filter());
  }

  protected onInputChange(value: string): void {
    this.searchSubject.next(value);
  }

  private updateModalCount(): void {
    this.modalCount = this.filter().modalFilters.selectedValuesCount();
  }
}
